import React from 'react';
import Pin from './Pin';
import './VerticalTimeline.css';

const MAX_STOPS = 10;

export default function VerticalTimeline({ stops }) {
  let displayStops = stops;
  let moreStopsCount = 0;

  if (stops.length > MAX_STOPS) {
    moreStopsCount = stops.length - 9;
    displayStops = [
      ...stops.slice(0, 8),
      { isMore: true, count: moreStopsCount },
      stops[stops.length - 1]
    ];
  }

  // Analyze stops to determine timetable structure
  const timetableStructure = React.useMemo(() => {
    // Find the maximum number of time lines across all stops
    const maxTimeLines = Math.max(...displayStops
      .filter(stop => !stop.isMore)
      .map(stop => stop.time ? stop.time.split('\n').length : 0)
    );

    // Determine size class based on number of services
    let sizeClass = 'small';
    if (maxTimeLines < 3) {
      sizeClass = 'large';
    } else if (maxTimeLines < 6) {
      sizeClass = 'medium';
    }

    return {
      maxTimeLines,
      sizeClass
    };
  }, [displayStops]);

  // Helper function to parse times for a stop
  const parseStopTimes = (stop) => {
    if (!stop.time) return { dropoffTimes: [], pickupTimes: [] };
    
    const dropoffTimes = [];
    const pickupTimes = [];
    
    // Check if this is a multi-trip charter (times separated by newlines)
    const isMultiTrip = stop.time.includes('\n');
    
    if (isMultiTrip) {
      // Multi-trip: times are separated by newlines
      const times = stop.time.split('\n').filter(time => time.trim() !== '');
      
      // For multi-trip, determine which times to show based on charterStopType
      if (stop.charterStopType === 'pickup') {
        // Only show pickup times
        pickupTimes.push(...times);
      } else if (stop.charterStopType === 'dropoff') {
        // Only show dropoff times
        dropoffTimes.push(...times);
      } else if (stop.charterStopType === 'both') {
        // Show both - assume even indices are drop-off and odd indices are pickup
        times.forEach((time, index) => {
          if (index % 2 === 0) {
            dropoffTimes.push(time);
          } else {
            pickupTimes.push(time);
          }
        });
      } else {
        // Default behavior for regular stops - assume even indices are drop-off and odd indices are pickup
        times.forEach((time, index) => {
          if (index % 2 === 0) {
            dropoffTimes.push(time);
          } else {
            pickupTimes.push(time);
          }
        });
      }
    } else {
      // Single-trip: time is already formatted as a single string
      // Determine which array to put it in based on charterStopType
      if (stop.charterStopType === 'pickup') {
        pickupTimes.push(stop.time);
      } else if (stop.charterStopType === 'dropoff') {
        dropoffTimes.push(stop.time);
      } else if (stop.charterStopType === 'both') {
        // For 'both' type with single time, we need to split it if it contains a range
        if (stop.time.includes(' - ')) {
          const [arriveTime, departTime] = stop.time.split(' - ');
          dropoffTimes.push(arriveTime.trim());
          pickupTimes.push(departTime.trim());
        } else {
          // If no range, treat as pickup time
          pickupTimes.push(stop.time);
        }
      } else {
        // Default behavior - treat as pickup time
        pickupTimes.push(stop.time);
      }
    }
    
    return { dropoffTimes, pickupTimes };
  };

  return (
    <div className={`vertical-timeline vertical-timeline-${timetableStructure.sizeClass}`}>
      {displayStops.map((stop, idx) => {
        if (stop.isMore) {
          return (
            <div className="timeline-stop-block" key={idx}>
              <div className="timeline-pin-column">
                <div className="pin-container">
                  <div className="more-stops-indicator">+{stop.count}</div>
                </div>
                <div className="timeline-connector" />
              </div>
              <div className="timeline-stop-content">
                <div className="more-stops-text">{stop.count} more stops</div>
              </div>
            </div>
          );
        }

        const { dropoffTimes, pickupTimes } = parseStopTimes(stop);
        const stopNumber = idx + 1;
        const hasDropoff = dropoffTimes.length > 0 && dropoffTimes.some(time => time.trim());
        const hasPickup = pickupTimes.length > 0 && pickupTimes.some(time => time.trim());
        const hasNotes = stop.notes && stop.notes.trim();

        return (
          <div className="timeline-stop-block" key={idx}>
            {/* Pin Column with connecting line */}
            <div className="timeline-pin-column">
              <div className="pin-container">
                <Pin
                  size={32}
                  type={
                    stop.charterStopType === 'pickup'
                      ? 'pickup'
                      : stop.charterStopType === 'dropoff'
                      ? 'dropoff'
                      : stop.charterStopType === 'both'
                      ? 'both'
                      : idx === 0
                      ? 'pickup'
                      : idx === displayStops.length - 1
                      ? 'dropoff'
                      : 'stop'
                  }
                  sequence={idx === displayStops.length - 1 && stops.length > MAX_STOPS ? stops.length : stopNumber}
                />
              </div>
              {/* Connecting line to next stop */}
              <div className="timeline-connector" />
            </div>

            {/* Stop Content Block */}
            <div className="timeline-stop-content">
              {/* Stop Name */}
              <div className="stop-header">
                <span className="stop-name">{stop.name || stop.stopName}</span>
              </div>

              {/* Drop-off Row */}
              {hasDropoff && (
                <div className="time-row dropoff-row">
                  <span className="time-label">Drop-off</span>
                  <div className="time-columns">
                    {Array.from({ length: timetableStructure.maxTimeLines }, (_, serviceIndex) => {
                      const time = dropoffTimes[serviceIndex];
                      return time && time.trim() ? (
                        <div key={serviceIndex} className="time-column">
                          {time}
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              {/* Pickup Row */}
              {hasPickup && (
                <div className="time-row pickup-row">
                  <span className="time-label">Pickup</span>
                  <div className="time-columns">
                    {Array.from({ length: timetableStructure.maxTimeLines }, (_, serviceIndex) => {
                      const time = pickupTimes[serviceIndex];
                      return time && time.trim() ? (
                        <div key={serviceIndex} className="time-column">
                          {time}
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              {/* Notes Row */}
              {hasNotes && (
                <div className="notes-row">
                  <span className="notes-content">{stop.notes}</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
